import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  Button,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter
} from '../ui';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { CheckCircleFilledIcon, XCircleFilledIcon, CircleIcon, FileIcon, PrinterIcon } from '../ui/Icons';
import { normalizeFileName } from '../ui/utils/fileNameEncoding';

/**
 * ViewModal - View record modal component
 * Extracted from GenericFormsPage for better maintainability
 */
const ViewModal = ({
  // State
  isOpen,
  selectedRecord,
  selectedRecordFiles,
  
  // Configuration
  config,
  fields,
  labels,
  
  // Callbacks
  onClose
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  if (!isOpen || !selectedRecord) {
    return null;
  }

  const renderFieldValue = (fieldName) => {
    const f = fields.find(ff => ff.name === fieldName);
    if (!f || f.type === 'hidden') return null;
    
    let rawVal = selectedRecord[fieldName];
    const isEmpty = rawVal == null || rawVal === "";

    // Compute display value for select/lookups and special types
    let displayVal = rawVal;
    let isBadge = false;
    let isStatus = false;
    let isBoolean = false;

    const displayField = f.table && f.table.displayField;
    if (displayField && selectedRecord[displayField] != null) {
      displayVal = selectedRecord[displayField];
      isBadge = f.table && (f.table.cellRenderer === 'badge' || f.table.cellRenderer === 'statusBadge');
      isStatus = f.table && f.table.cellRenderer === 'statusBadge';
    } else if (/Id$/.test(f.name)) {
      const base = f.name.replace(/Id$/, '');
      const rel = selectedRecord[base];
      if (rel && typeof rel === 'object') {
        displayVal = rel.name || rel.title || rel.status || rel.label || displayVal;
        isBadge = true;
      }
    }

    if ((f.type === 'switch') || f.type === 'boolean') {
      isBoolean = true;
    } else if (f.type === 'date') {
      try { 
        if (!isEmpty) displayVal = new Date(rawVal).toLocaleDateString(); 
      } catch {}
    }

    return (
      <div key={fieldName}>
        <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
        <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
          {isBoolean ? (
            (rawVal === true) ? (
              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                <CheckCircleFilledIcon className="w-4 h-4" />
                <span>Yes</span>
              </span>
            ) : (rawVal === false) ? (
              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                <XCircleFilledIcon className="w-4 h-4" />
                <span>No</span>
              </span>
            ) : (
              <span className={cn("inline-flex items-center gap-1", theme.text.muted)}>
                <CircleIcon className="w-4 h-4" />
                <span>—</span>
              </span>
            )
          ) : isBadge || isStatus ? (
            <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
              isStatus ? theme.colors.info : theme.colors.primary,
              theme.text.inverse
            )}>
              {String(displayVal ?? '—')}
            </span>
          ) : f.type === 'textarea' ? (
            <span className="whitespace-pre-wrap">{String(displayVal ?? '—')}</span>
          ) : (
            <span className={cn(isEmpty && theme.text.muted)}>{String(displayVal ?? '—')}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader onClose={onClose}>
        <ModalTitle>{labels.viewTitle}</ModalTitle>
      </ModalHeader>
      <ModalContent maxHeight="96">
        <div className="grid grid-cols-1 gap-4 overflow-auto">
          {/* File Attachments Section */}
          {selectedRecordFiles && selectedRecordFiles.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileIcon className="w-5 h-5 text-blue-500" />
                <div className={cn("text-sm font-semibold", theme.text.primary)}>
                  File Attachments ({selectedRecordFiles.length})
                </div>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {selectedRecordFiles.map((file, index) => (
                  <div key={index} className={cn("flex items-center gap-3 p-3 rounded-lg border", theme.colors.background, theme.border.subtle)}>
                    <FileIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={cn("text-sm font-medium truncate", theme.text.primary)}>
                        {normalizeFileName(file.fileName || '')}
                      </div>
                      <div className={cn("text-xs", theme.text.secondary)}>
                        {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.fileType || 'Unknown type'}
                      </div>
                    </div>
                    <a 
                      href={API_ENDPOINTS.fileAttachment.download(file.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn("text-xs px-3 py-1 rounded-md transition-colors", theme.colors.primary, theme.text.inverse, "hover:opacity-80")}
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {config.formSections?.map((section, idx) => (
            <div key={idx}>
              <div className="flex items-center gap-2 mb-2">
                {section.icon && (
                  <span className="text-base" aria-hidden="true">{section.icon}</span>
                )}
                <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
              </div>
              {section.layout === 'custom' && section.customComponent ? (
                // Render custom component for view mode
                <section.customComponent
                  deviationId={selectedRecord?.id}
                  formData={selectedRecord}
                  setFormData={undefined}
                  config={config}
                  fields={fields}
                  isEditMode={false}
                  selectedRecord={selectedRecord}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {section.fields.map((fieldName) => renderFieldValue(fieldName))}
                </div>
              )}
            </div>
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex items-center gap-3">
          {selectedRecord?.id && config?.entityName?.toLowerCase() === 'complaint' && (
            <Button 
              onClick={() => {
                navigate(`/complaint/${selectedRecord.id}/print`);
                onClose();
              }}
              variant="outline"
            >
              <PrinterIcon className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          )}
          {selectedRecord?.id && config?.entityName?.toLowerCase() === 'riskassessment' && (
            <Button 
              onClick={() => {
                navigate(`/risk-assessment/${selectedRecord.id}/print`);
                onClose();
              }}
              variant="outline"
            >
              <PrinterIcon className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          )}
          <Button onClick={onClose}>
            {labels.closeText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ViewModal;
